/**
 * Does this session satisfy what a screen asks for?
 *
 * A route or a navigation entry declares `permission`, and the shell has to
 * decide whether to draw it. Almost always that is one permission and the
 * question is trivial — but not always, and the exception is the reason this
 * file exists.
 *
 * A screen can legitimately be reachable through two different rights. The
 * file of a work order is the case that found this: the office opens it from
 * the list with `workorders:read`, and the technician opens the very same
 * screen from «Mi día» with `workorders:report`, which is the narrower right
 * that lets somebody see their own van without browsing the whole company's
 * work. The backend has always said so — `permission('workorders:read',
 * 'workorders:report')` takes several and means ANY of them — and the module
 * wrote the same thing in its route: `permission: ['workorders:read',
 * 'workorders:report']`.
 *
 * The shell did not understand it. `auth.can()` answers about ONE permission,
 * so it was asked whether the session holds a permission literally named
 * `workorders:read,workorders:report`, answered no, and the guard sent the
 * technician back to the home screen — silently, because a redirect is not an
 * error. Three detail screens (work order, task, incident) were unreachable
 * for everybody except a superadmin, whose role short-circuits the check.
 *
 * So the rule lives here rather than in the store: `can()` stays the primitive
 * that answers about one permission, and this composes it into the question a
 * screen actually asks. Both callers — the router guard and the sidebar filter
 * — go through it, because a screen you can see and cannot open and a screen
 * you can open and cannot see are the same bug seen from two sides.
 *
 * @param {string|string[]|null|undefined} required  what the screen declares
 * @param {(permission: string) => boolean} can      the session's answer for one
 * @returns {boolean}
 */
/** Where a session goes when the server holds its permissions back. */
export const PASSWORD_CHANGE_ROUTE = 'profile'

/**
 * Does this session have to change its password before anything else?
 *
 * While `mustChangePassword` is set the server resolves the session with no
 * permissions at all (platform/auth, SessionRegistry). Without this detour the
 * shell draws «Inicio» and nothing else, and says nothing: that is what five of
 * Seguiment Cerdanya's six accounts saw after 0.13.0, because only the login
 * screen looked at the flag and their sessions were already open.
 *
 * Asked by the router guard on every navigation and by the shell when a fresh
 * profile arrives, so a session that learns it late is moved as well.
 *
 * @param {{ mustChangePassword?: boolean }|null|undefined} user  the profile
 * @param {{ name?: string|symbol }} to                           the route asked for
 * @returns {{ name: string }|null} where to go instead, or null to carry on
 */
export function passwordChangeDetour(user, to) {
  if (!user?.mustChangePassword) return null
  if (to?.name === PASSWORD_CHANGE_ROUTE) return null
  return { name: PASSWORD_CHANGE_ROUTE }
}

export function allows(required, can) {
  // Nothing declared: any signed-in user. That is the common case and the
  // reason most screens say nothing at all.
  if (!required) return true

  if (Array.isArray(required)) {
    // ANY, like the backend. An empty list is not "everybody" — it is a screen
    // whose author meant to name something and did not, and letting it through
    // would open it to the whole company.
    return required.length > 0 && required.some(permission => can(permission))
  }

  return can(required)
}
